import { NgFor } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import * as BoondockActions from '@curbnturf/boondock-listing/src/lib/+state/boondock.actions';
import { BoondockFacade } from '@curbnturf/boondock-listing/src/lib/+state/boondock.facade';
import { Boondock, IUser } from '@curbnturf/entities';
import {
  DisplayValidationErrorsDirective,
  DisplayValidationErrorsSubmitButtonDirective,
  DisplayValidationErrorsSubmitDirective
} from '@curbnturf/form-helpers';
import { SignUpStateFacade } from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, LoadingController, ModalController } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-boondock-add-modal',
  templateUrl: './boondock-add-modal.component.html',
  styleUrls: ['./boondock-add-modal.component.scss'],
  standalone: true,
  imports: [
    DisplayValidationErrorsDirective,
    DisplayValidationErrorsSubmitButtonDirective,
    DisplayValidationErrorsSubmitDirective,
    FormsModule,
    IonicModule,
    NgSelectModule,
    NgFor,
    ReactiveFormsModule,
  ],
})
export class BoondockAddModalComponent implements OnInit, OnDestroy {
  @Input() currentBoondocks: Boondock[];

  duplicateForm: FormGroup<{ duplicateBoondock: FormControl<number | null> }>;

  currentUser: IUser;

  private subscriptions: Subscription[] = [];

  constructor(
    private modalController: ModalController,
    private router: Router,
    private boondockFacade: BoondockFacade,
    private fb: FormBuilder,
    private actions$: Actions,
    private loadingCtrl: LoadingController,
    private signUpFacade: SignUpStateFacade,
    private userFacade: UserFacade,
  ) {
    this.duplicateForm = this.fb.group({ duplicateBoondock: this.fb.control<number | null>(null) });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        this.currentUser = user;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  async close() {
    await this.modalController.dismiss();
  }

  async add(): Promise<void> {
    await this.close();
    this.signUpFacade.startNewBoondock(this.currentUser);
  }

  async duplicate(): Promise<void> {
    if (this.duplicateForm.value.duplicateBoondock) {
      const boondockId = this.duplicateForm.value.duplicateBoondock;
      const selectedBoondock = this.currentBoondocks.find((el) => el.id === boondockId);
      if (selectedBoondock) {
        const loading = await this.loadingCtrl.create({
          message: 'Duplicating Boondock...',
          duration: 10000,
        });

        this.subscriptions.push(
          this.actions$.pipe(ofType(BoondockActions.boondockCreated), take(1)).subscribe(async (newBoondock) => {
            if (newBoondock.boondock) {
              await this.router.navigate(['', 'properties', 'boondocking', 'edit', newBoondock.boondock.id]);
              await loading.dismiss();
              await this.close();
            }
          }),
        );

        const  duplicateBoondock = {...selectedBoondock};

        delete duplicateBoondock.id;
        delete duplicateBoondock.syncId;

        this.boondockFacade.create(duplicateBoondock);
        await loading.present();
      }
    }
  }
}
